import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  FloatingDialogComponentType,
  FloatingDialogInstance,
} from '../models/floating-dialog.models';

const CASCADE_OFFSET = 24;
const CASCADE_MAX = 8;

// Mirror the .floating-dialog-wrapper sizing in
// floating-dialog-container.component.scss: width min(960px, 92vw),
// height min(744px, 100vh - header - spacing). Needed to compute a
// centered position before the dialog is rendered.
const DIALOG_MAX_WIDTH = 960;
const DIALOG_VIEWPORT_WIDTH_RATIO = 0.92;
const DIALOG_MAX_HEIGHT = 744;
const FALLBACK_HEADER_HEIGHT = 99;

export interface FloatingDialogOpenOptions {
  /** Center the dialog in the viewport instead of the cascading default. */
  centered?: boolean;
  /**
   * Scale the dialog's default opening height, e.g. 2 / 3 opens at
   * two-thirds height. The dialog remains user-resizable.
   */
  heightRatio?: number;
  /**
   * Scale the dialog's default opening width, e.g. 7 / 6 opens 1/6 wider
   * than default. The dialog remains user-resizable.
   */
  widthRatio?: number;
  /**
   * Optional identity used to prevent stacking duplicate dialogs. When a
   * dialog with the same key is already open, `open` focuses (and restores)
   * that instance instead of creating a second, overlapping one.
   */
  key?: string;
}

@Injectable({ providedIn: 'root' })
export class FloatingDialogService {
  private readonly _dialogs = new BehaviorSubject<FloatingDialogInstance[]>([]);
  readonly dialogs$ = this._dialogs.asObservable();

  private nextZIndex = 1;

  open(
    component: FloatingDialogComponentType,
    title: string,
    inputs?: Record<string, unknown>,
    options?: FloatingDialogOpenOptions,
  ): void {
    const dialogs = this._dialogs.getValue();
    // Guard against stacking duplicate dialogs: if one with the same key is
    // already open, bring it to the front (restoring it if minimized) rather
    // than opening a second, perfectly overlapping instance.
    if (options?.key) {
      const existing = dialogs.find((d) => d.key === options.key);
      if (existing) {
        this._update(existing.id, {
          minimized: false,
          zIndex: ++this.nextZIndex,
        });
        return;
      }
    }
    const cascade = (dialogs.length % CASCADE_MAX) * CASCADE_OFFSET;
    const id = crypto.randomUUID();
    const headerHeight = this.headerHeight();
    // Scale the defaults by the requested ratios, but clamp back to the
    // viewport-derived maximums so a ratio > 1 can't open the dialog wider
    // than 92vw or taller than the space below the header.
    const height = options?.heightRatio
      ? Math.min(
          Math.round(
            this.defaultDialogHeight(headerHeight) * options.heightRatio,
          ),
          window.innerHeight - headerHeight,
        )
      : undefined;
    const width = options?.widthRatio
      ? Math.min(
          Math.round(this.defaultDialogWidth() * options.widthRatio),
          window.innerWidth * DIALOG_VIEWPORT_WIDTH_RATIO,
        )
      : undefined;
    // Auto-inject the dialog id so the loaded component can opt into
    // dialog-aware behaviour (e.g. detect dialog mode, close itself).
    const instance: FloatingDialogInstance = {
      id,
      title,
      component,
      key: options?.key,
      inputs: { ...(inputs ?? {}), floatingDialogId: id },
      position: options?.centered
        ? this.centeredPosition(headerHeight, height, width)
        : { x: 80 + cascade, y: 80 + cascade },
      minimized: false,
      zIndex: ++this.nextZIndex,
      height,
      width,
    };
    this._dialogs.next([...dialogs, instance]);
  }

  private headerHeight(): number {
    return (
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--header-height',
        ),
        10,
      ) || FALLBACK_HEADER_HEIGHT
    );
  }

  private defaultDialogHeight(headerHeight: number): number {
    return Math.min(DIALOG_MAX_HEIGHT, window.innerHeight - headerHeight);
  }

  private defaultDialogWidth(): number {
    return Math.min(
      DIALOG_MAX_WIDTH,
      window.innerWidth * DIALOG_VIEWPORT_WIDTH_RATIO,
    );
  }

  private centeredPosition(
    headerHeight: number,
    height?: number,
    width?: number,
  ): { x: number; y: number } {
    const dialogWidth = width ?? this.defaultDialogWidth();
    const dialogHeight = height ?? this.defaultDialogHeight(headerHeight);
    return {
      x: Math.max(0, Math.round((window.innerWidth - dialogWidth) / 2)),
      // Keep the title bar below the fixed app header.
      y: Math.max(
        headerHeight,
        Math.round((window.innerHeight - dialogHeight) / 2),
      ),
    };
  }

  close(id: string): void {
    this._dialogs.next(this._dialogs.getValue().filter((d) => d.id !== id));
  }

  focus(id: string): void {
    this._update(id, { zIndex: ++this.nextZIndex });
  }

  minimize(id: string): void {
    this._update(id, { minimized: true });
  }

  restore(id: string): void {
    this._update(id, { minimized: false });
  }

  updatePosition(id: string, position: { x: number; y: number }): void {
    this._update(id, { position });
  }

  private _update(id: string, changes: Partial<FloatingDialogInstance>): void {
    this._dialogs.next(
      this._dialogs.getValue().map((d) => (d.id === id ? { ...d, ...changes } : d)),
    );
  }
}
